import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  SettingsField,
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsTable,
  SettingsTabs,
  SettingsToggleRow,
} from "@/components/settings/settings-ui";
import {
  useMeetingPreferences,
  useUpdateMeetingPreferences,
} from "@/hooks/useMeetings";
import { VisitorsSettingsPanel } from "@/components/visitors/visitors-settings-panel";

function usePersistToggles(key: string, defaults: Record<string, boolean>) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return { ...defaults, ...(JSON.parse(raw) as Record<string, boolean>) };
    } catch {
      /* ignore */
    }
    return defaults;
  });
  function set(id: string, value: boolean) {
    setState((prev) => {
      const next = { ...prev, [id]: value };
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }
  return [state, set] as const;
}

/** @deprecated Use real page at settings/mailboxes.page.tsx */
export { SettingsMailboxesPage } from "./mailboxes.page";

export function SettingsNotificationsPage() {
  const [toggles, setToggle] = usePersistToggles("settings.notifications", {
    dataEmail: true,
    dataSlack: false,
    dataDigest: true,
    taskEmail: true,
    taskSlack: false,
  });
  return (
    <div>
      <SettingsPageHeader
        title="Notifications"
        description="Select the notifications you want to receive after data requests and task due dates."
        onSave={() => undefined}
      />
      <SettingsSection
        title="System activity"
        description="Keep track of data requests under Settings → System activity → Data requests."
      >
        <p className="text-xs font-semibold uppercase text-muted-foreground">Email</p>
        <SettingsToggleRow
          label="Receive a notification every time a data request is completed"
          checked={toggles.dataEmail}
          onChange={(v) => setToggle("dataEmail", v)}
        />
        <SettingsToggleRow
          label="Receive a daily digest of data requests completed in a day"
          checked={toggles.dataDigest}
          onChange={(v) => setToggle("dataDigest", v)}
        />
        <p className="pt-2 text-xs font-semibold uppercase text-muted-foreground">Slack</p>
        <SettingsToggleRow
          label="Post data-request completions to Slack"
          checked={toggles.dataSlack}
          onChange={(v) => setToggle("dataSlack", v)}
        />
      </SettingsSection>
      <SettingsSection title="Tasks">
        <p className="text-xs font-semibold uppercase text-muted-foreground">Email</p>
        <SettingsToggleRow
          label="Receive reminders when tasks are due within a day"
          checked={toggles.taskEmail}
          onChange={(v) => setToggle("taskEmail", v)}
        />
        <p className="pt-2 text-xs font-semibold uppercase text-muted-foreground">Slack</p>
        <SettingsToggleRow
          label="Receive task reminders in Slack"
          checked={toggles.taskSlack}
          onChange={(v) => setToggle("taskSlack", v)}
        />
        <Link to="/tasks" className="text-sm text-brand hover:underline">
          Open Tasks
        </Link>
      </SettingsSection>
    </div>
  );
}

export function SettingsChromeExtensionPage() {
  const [toggles, setToggle] = usePersistToggles("settings.chrome", {
    tracking: true,
    desktop: true,
    reminder: false,
  });
  return (
    <div>
      <SettingsPageHeader title="Chrome extension" onSave={() => undefined} />
      <SettingsSection title="Chrome extension settings">
        <SettingsToggleRow
          label="Enable email tracking, templates, send later, and reminders"
          checked={toggles.tracking}
          onChange={(v) => setToggle("tracking", v)}
        />
        <SettingsToggleRow
          label="Enable desktop notifications for email opens & clicks"
          checked={toggles.desktop}
          onChange={(v) => setToggle("desktop", v)}
        />
        <SettingsToggleRow
          label="Send reminder if there is no reply by default"
          checked={toggles.reminder}
          onChange={(v) => setToggle("reminder", v)}
        />
      </SettingsSection>
    </div>
  );
}

export function SettingsConversationsRecordingPage() {
  const [toggles, setToggle] = usePersistToggles("settings.conversations", {
    zoom: true,
    meet: true,
    teams: false,
    zoomCloud: false,
    internalHost: true,
    internalParticipant: false,
    externalHost: true,
    externalParticipant: true,
  });
  return (
    <div>
      <SettingsPageHeader
        title="Conversations"
        description="Video conferencing and meeting recording settings. Admins can change company-wide defaults."
      />
      <SettingsTabs tabs={["Recording settings"]} active="Recording settings" onChange={() => undefined} />
      <SettingsSection
        title="Video conferencing apps"
        description="Based on your auto-record settings, the recorder joins meetings with links in your calendar events."
      >
        <p className="text-xs font-medium text-muted-foreground">Record via the Apollo Recorder</p>
        <SettingsToggleRow label="Zoom" checked={toggles.zoom} onChange={(v) => setToggle("zoom", v)} />
        <SettingsToggleRow
          label="Google Meet"
          checked={toggles.meet}
          onChange={(v) => setToggle("meet", v)}
        />
        <SettingsToggleRow
          label="Microsoft Teams"
          checked={toggles.teams}
          onChange={(v) => setToggle("teams", v)}
        />
        <p className="pt-2 text-xs font-medium text-muted-foreground">Record via Zoom Cloud</p>
        <SettingsToggleRow
          label="Zoom Cloud"
          description="Requires a Zoom paid plan and connected Zoom account."
          checked={toggles.zoomCloud}
          onChange={(v) => setToggle("zoomCloud", v)}
        />
      </SettingsSection>
      <SettingsSection
        title="Auto-record meetings"
        description="Select which meetings the recorder will automatically join and record."
      >
        <p className="text-xs font-medium text-muted-foreground">
          Record my team&apos;s internal meetings when
        </p>
        <SettingsToggleRow
          label="A recorded user is the host"
          checked={toggles.internalHost}
          onChange={(v) => setToggle("internalHost", v)}
        />
        <SettingsToggleRow
          label="A recorded user is a participant"
          checked={toggles.internalParticipant}
          onChange={(v) => setToggle("internalParticipant", v)}
        />
        <p className="pt-2 text-xs font-medium text-muted-foreground">
          Record my team&apos;s external meetings when
        </p>
        <SettingsToggleRow
          label="A recorded user is the host"
          checked={toggles.externalHost}
          onChange={(v) => setToggle("externalHost", v)}
        />
        <SettingsToggleRow
          label="A recorded user is a participant"
          checked={toggles.externalParticipant}
          onChange={(v) => setToggle("externalParticipant", v)}
        />
        <p className="text-xs text-muted-foreground">
          Tip: connect Google Calendar under Integrations so meetings appear in Meetings.
        </p>
        <Link to="/settings/integrations" className="text-sm text-brand hover:underline">
          Open Integrations
        </Link>
      </SettingsSection>
    </div>
  );
}

/** @deprecated Use settings/deliverability.page.tsx */
export { SettingsDeliverabilityPage } from "./deliverability.page";

export function SettingsBillingPage() {
  const { pathname } = useLocation();
  const title = useMemo(() => {
    if (pathname.includes("features-add-ons")) return "Product Add-ons";
    if (pathname.includes("license")) return "License settings";
    if (pathname.includes("ai-runs")) return "AI run usage";
    if (pathname.includes("credits")) return "Credits & AI usage";
    return "Plan overview";
  }, [pathname]);

  return (
    <div>
      <SettingsPageHeader title={title} />
      <SettingsSection title="Plan">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-border p-3">
            <p className="text-[11px] uppercase text-muted-foreground">Plan</p>
            <p className="text-lg font-semibold">Workspace</p>
          </div>
          <div className="rounded-md border border-border p-3">
            <p className="text-[11px] uppercase text-muted-foreground">Credits</p>
            <p className="text-lg font-semibold">—</p>
          </div>
          <div className="rounded-md border border-border p-3">
            <p className="text-[11px] uppercase text-muted-foreground">AI usage</p>
            <p className="text-lg font-semibold">—</p>
          </div>
        </div>
        <Link to="/settings/ai" className="text-sm text-brand hover:underline">
          Configure AI providers
        </Link>
      </SettingsSection>
    </div>
  );
}

export function SettingsTeamStubPage({
  title,
  description,
  columns,
}: {
  title: string;
  description: string;
  columns: string[];
}) {
  return (
    <div>
      <SettingsPageHeader title={title} description={description} />
      <SettingsSection title={title}>
        <SettingsTable columns={columns} rows={[]} empty={`No ${title.toLowerCase()} yet.`} />
      </SettingsSection>
    </div>
  );
}

export function SettingsIntentScoringPage() {
  const { pathname } = useLocation();
  const isScoring = pathname.includes("scoring");
  return (
    <div>
      <SettingsPageHeader
        title={isScoring ? "Scoring" : "Buying intent"}
        description={
          isScoring
            ? "Score people and companies against your ICP to prioritize outreach."
            : "Configure intent topics and thresholds for account prioritization."
        }
      />
      <SettingsSection title={isScoring ? "Scoring models" : "Intent topics"}>
        <SettingsTable
          columns={isScoring ? ["Model", "Object", "Updated"] : ["Topic", "Strength", "Status"]}
          rows={[]}
          empty={
            isScoring
              ? "No scoring models yet. Use Personas and Signals meanwhile."
              : "No buying-intent topics configured."
          }
        />
        <Link to="/settings/personas" className="text-sm text-brand hover:underline">
          Open Personas
        </Link>
      </SettingsSection>
    </div>
  );
}

export function SettingsWebsiteVisitorsSettingsPage() {
  return (
    <div>
      <SettingsPageHeader
        title="Website visitors"
        description="Identify companies visiting your site and sync them into Find & enrich."
      />
      <SettingsSection title="Pixel & tracking">
        <VisitorsSettingsPanel compact />
        <Link
          to="/visitors/settings"
          className="mt-4 inline-block text-sm text-brand hover:underline"
        >
          Open full Website visitors settings
        </Link>
      </SettingsSection>
    </div>
  );
}

export function SettingsSequencesConfigPage() {
  const { pathname } = useLocation();
  if (pathname.includes("snippets")) {
    return (
      <div>
        <SettingsPageHeader title="Snippets" description="Reusable text snippets for email steps." />
        <SettingsSection title="Snippets">
          <SettingsTable columns={["Name", "Owner", "Updated"]} rows={[]} empty="No snippets yet." />
        </SettingsSection>
      </div>
    );
  }
  if (pathname.includes("tracking")) {
    return (
      <div>
        <SettingsPageHeader
          title="Tracking"
          description="Tracking subdomains used for open and click tracking."
        />
        <SettingsSection title="Tracking subdomains">
          <SettingsTable
            columns={["Subdomain", "Status", "CNAME"]}
            rows={[]}
            empty="No tracking subdomains configured."
          />
        </SettingsSection>
      </div>
    );
  }
  return null;
}

export function SettingsTeamConversationsAdminPage() {
  const { pathname } = useLocation();
  const leaf = pathname.split("/").pop() ?? "team-conversations";
  const titleMap: Record<string, string> = {
    "team-conversations": "Recording configuration",
    users: "Team permissions",
    trackers: "Trackers",
    scorecards: "Scorecards",
    "custom-field-prompts": "Custom field prompts",
  };
  const title = titleMap[leaf] ?? "Team conversations";
  return (
    <div>
      <SettingsPageHeader title={title} description="Company-wide conversation intelligence settings." />
      <SettingsSection title={title}>
        <SettingsTable columns={["Name", "Status", "Updated"]} rows={[]} empty="Nothing configured yet." />
        <Link to="/conversations" className="text-sm text-brand hover:underline">
          Open Conversations
        </Link>
      </SettingsSection>
    </div>
  );
}

export function SettingsTeamMeetingsPage() {
  const prefsQuery = useMeetingPreferences();
  const updateMut = useUpdateMeetingPreferences();
  const prefs = prefsQuery.data?.preferences;

  return (
    <div>
      <SettingsPageHeader
        title="Team meetings"
        description="Defaults for scheduling and calendar sync for your workspace."
      />
      <SettingsSection title="Calendar">
        {prefsQuery.isLoading || !prefs ? (
          <p className="text-sm text-muted-foreground">Loading preferences…</p>
        ) : (
          <>
            <SettingsToggleRow
              label="Sync calendar events to Meetings"
              checked={prefs.calendarSyncEnabled}
              onChange={(checked) =>
                updateMut.mutate({ calendarSyncEnabled: checked })
              }
            />
            <SettingsToggleRow
              label="Create engagement events when meetings are booked"
              checked={prefs.createEngagementOnBook}
              onChange={(checked) =>
                updateMut.mutate({ createEngagementOnBook: checked })
              }
            />
            <SettingsField label="Default personal booking slug">
              <SettingsInput
                value={prefs.personalSlug}
                readOnly
              />
            </SettingsField>
            <p className="text-xs text-muted-foreground">
              Edit timezone, weekly hours, and slug under{" "}
              <Link to="/meetings/availability" className="text-brand hover:underline">
                Meetings → Availability
              </Link>
              .
            </p>
          </>
        )}
        <div className="flex gap-3 text-sm">
          <Link to="/settings/integrations" className="text-brand hover:underline">
            Connect Google Calendar
          </Link>
          <Link to="/meetings/scheduled" className="text-brand hover:underline">
            Open Meetings
          </Link>
        </div>
      </SettingsSection>
    </div>
  );
}

export function SettingsTeamSharingPage() {
  return (
    <div>
      <SettingsPageHeader
        title="Team sharing & defaults"
        description="Default ownership and sharing rules for people and companies."
      />
      <SettingsTabs
        tabs={["People", "Companies"]}
        active="People"
        onChange={() => undefined}
      />
      <SettingsSection title="Default sharing">
        <SettingsField label="New people are owned by">
          <SettingsInput value="Record creator" readOnly />
        </SettingsField>
        <SettingsToggleRow
          label="Share new people with my team by default"
          checked={false}
          onChange={() => undefined}
        />
      </SettingsSection>
    </div>
  );
}

export function SettingsSystemActivityPage() {
  const { pathname } = useLocation();
  const isLog = pathname.includes("activity-log");
  return (
    <div>
      <SettingsPageHeader
        title={isLog ? "System activity log" : "Data requests"}
        description={
          isLog
            ? "Audit trail of workspace configuration and sync activity."
            : "History of enrichment and data pull requests."
        }
      />
      <SettingsSection title={isLog ? "Activity log" : "Data requests"}>
        <SettingsTable
          columns={
            isLog
              ? ["Time", "Actor", "Action", "Target"]
              : ["Requested", "Type", "Status", "Completed"]
          }
          rows={[]}
          empty="No activity yet."
        />
      </SettingsSection>
    </div>
  );
}

export function SettingsObjectsFieldsPage() {
  const { pathname } = useLocation();
  const object = pathname.includes("leads")
    ? "Lead"
    : pathname.includes("accounts")
      ? "Account"
      : "Deal";
  return (
    <div>
      <SettingsPageHeader
        title={`${object} fields & stages`}
        description={`Manage ${object.toLowerCase()} fields and pipeline stages for your workspace.`}
      />
      <SettingsTabs tabs={["Fields", "Stages"]} active="Fields" onChange={() => undefined} />
      <SettingsSection title={`${object} fields`}>
        <SettingsTable
          columns={["Field", "Type", "Group", "Required"]}
          rows={
            object === "Deal"
              ? [
                  { Field: "Deal name", Type: "text", Group: "Core", Required: "Yes" },
                  { Field: "Amount", Type: "number", Group: "Core", Required: "No" },
                  { Field: "Stage", Type: "stage", Group: "Core", Required: "Yes" },
                  { Field: "Close date", Type: "date", Group: "Core", Required: "No" },
                ]
              : []
          }
          empty={`No custom ${object.toLowerCase()} fields yet.`}
        />
        {object === "Deal" && (
          <Link to="/settings/opportunities" className="text-sm text-brand hover:underline">
            View deal stages
          </Link>
        )}
      </SettingsSection>
    </div>
  );
}

/** @deprecated Prefer imports-exports.page — kept to avoid breaking re-exports */
export { SettingsImportsExportsPage } from "./imports-exports.page";

export function SettingsRemovalRequestsPage() {
  return (
    <div>
      <SettingsPageHeader
        title="Removal requests"
        description="Review requests to remove leads or accounts from your workspace."
      />
      <SettingsSection title="Open requests">
        <SettingsTable
          columns={["Requested", "Object", "Requester", "Status"]}
          rows={[]}
          empty="No removal requests."
        />
      </SettingsSection>
    </div>
  );
}

export function SettingsTeamsPage() {
  return (
    <SettingsTeamStubPage
      title="Teams"
      description="Group users for reporting filters and shared sequence ownership."
      columns={["Team", "Members", "Updated"]}
    />
  );
}

export function SettingsPermissionProfilesPage() {
  return (
    <SettingsTeamStubPage
      title="Permission profiles"
      description="Control what teammates can access across Find & enrich, Engage, Win, and Settings."
      columns={["Profile", "Users", "Updated"]}
    />
  );
}

export function SettingsSecurityPage() {
  return (
    <div>
      <SettingsPageHeader title="Security" description="SSO, MFA, IP allowlists, and support access." />
      <SettingsSection title="Security controls">
        <SettingsToggleRow
          label="Require MFA for all users"
          checked={false}
          onChange={() => undefined}
        />
        <SettingsToggleRow
          label="Allow password login"
          checked
          onChange={() => undefined}
        />
        <Link to="/settings/account" className="text-sm text-brand hover:underline">
          Manage your MFA on Profile
        </Link>
      </SettingsSection>
    </div>
  );
}
